<!-- Page Header -->
<app-page-header></app-page-header>
<app-style-view [mainColor]="'#00733f'"></app-style-view>

<!-- Main Content -->
<div class="container py-2">
  <div class="row">

    <!-- Sidebar -->
    <div class="col-md-3">
      <div class="card border-radius-0 bg-color-light border-0 box-shadow-2 mb-4">
        <div class="card-body p-0">

          <!-- Search Type Header -->
          <div class="search-header">
            <div class="search-icon-wrapper">
              <i class="fas fa-lg" [ngClass]="searchType === 'classification' ? 'fa-layer-group' : 'fa-tag'"></i>
            </div>
            <h3 class="search-title">{{ getSearchTypeLabel() }}</h3>
          </div>

          <!-- Keyword Display -->
          <div class="keyword-card">
            <div class="keyword-label">
              <i class="fas fa-search mr-1"></i>
              {{ searchType === 'classification' ? ('CLASSIFICATION' | translate) : ('KEYWORD' | translate) }}
            </div>
            <div>
              <span class="keyword-value">"{{ keyword }}"</span>
            </div>
          </div>

          <!-- Results Counter -->
          @if (!isLoading && !error && articles.length > 0) {
            <div class="results-counter">
              <div class="counter-circle">
                <div class="counter-number">{{ articles.length }}</div>
              </div>
              <div class="counter-label">
                {{ 'KEYWORD_SEARCH_RESULTS_FOUND' | translate }}
              </div>
            </div>
          }

          <!-- No Results Message -->
          @if (!isLoading && !error && articles.length === 0) {
            <div class="no-results-message">
              <i class="fas fa-inbox fa-2x mb-2"></i>
              <div class="text-muted small">{{ 'NO_RESULTS' | translate }}</div>
            </div>
          }

        </div>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="col-md-9">

      <!-- Loading State -->
      @if (isLoading) {
        <div class="card border-radius-0 bg-color-light border-0 box-shadow-2 mb-4">
          <div class="card-body text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">{{ 'LOADING' | translate }}</span>
            </div>
            <p class="mt-3 mb-0 text-muted">{{ 'KEYWORD_SEARCH_LOADING' | translate }}</p>
          </div>
        </div>
      }

      <!-- Error State -->
      @if (!isLoading && error) {
        <div class="card border-radius-0 bg-color-light border-0 box-shadow-2 mb-4">
          <div class="card-body text-center py-5">
            <i class="fas fa-exclamation-triangle text-danger mb-3" style="font-size: 3rem;"></i>
            <h4>{{ 'ERROR' | translate }}</h4>
            <p class="text-muted">{{ error }}</p>
          </div>
        </div>
      }

      <!-- No Results -->
      @if (!isLoading && !error && articles.length === 0) {
        <div class="card border-radius-0 bg-color-light border-0 box-shadow-2 mb-4">
          <div class="card-body text-center py-5">
            <i class="fas fa-search text-muted mb-3" style="font-size: 3rem;"></i>
            <h4>{{ 'KEYWORD_SEARCH_NO_RESULTS' | translate }}</h4>
            <p class="text-muted">{{ 'KEYWORD_SEARCH_NO_RESULTS_DESC' | translate: {keyword: keyword} }}</p>
          </div>
        </div>
      }

      <!-- Articles List -->
      @if (!isLoading && !error && articles.length > 0) {
        <div>
          <article class="post">
            <div class="row">
              @for (article of articles; track article; let i = $index) {
                <div class="col-md-12">
                  <div class="article-container">
                    <div class="post-content">
                      <!-- Article Title -->
                      <h5>
                        <a class="from-journal article-title-link"
                          [routerLink]="['/journal', article.journalSlug, 'article', article.slug]"
                          [queryParams]="getArticleQueryParams(article)"
                          [title]="article.title"
                          style="cursor: pointer;">
                          {{ article.title }}
                        </a>
                      </h5>
                      <!-- Article Abstract -->
                      @if (article.abstract) {
                        <div
                          class="mb-1 abstract"
                          [innerHTML]="getFormattedAbstract(article.abstract)">
                        </div>
                      }
                      <!-- Authors -->
                      @if (article.authors && article.authors.length > 0) {
                        <div class="post-meta">
                          <span class="authors-list">
                            @for (author of article.authors; track author; let authorIndex = $index) {
                              <span>
                                <app-author-view [author]="author"></app-author-view>
                                @if (authorIndex < article.authors.length - 1) {
                                  <span>,&nbsp;</span>
                                }
                              </span>
                            }
                          </span>
                        </div>
                      }
                      <!-- Article Metadata -->
                      <div class="post-meta">
                        <div class="row">
                          <!-- DOI Section -->
                          @if (article.doi) {
                            <div class="col-md-12">
                              <div class="doi-container">
                                <span class="doi-label from-journal">
                                  DOI :
                                </span>
                                <a class="doi-value"
                                  [title]="'DOI'"
                                  [href]="'https://doi.org/' + article.doi"
                                  target="_blank">
                                  {{ article.doi }}
                                </a>
                              </div>
                            </div>
                          }
                          <!-- Badges Section -->
                          <div class="col-md-12 text-right mt-1">
                            <div class="badges-container">
                              <!-- Journal Name Badge -->
                              @if (article.journalName) {
                                <span
                                  class="badge badge-custom badge-sm mr-2">
                                  {{ article.journalName }}
                                </span>
                              }
                              <!-- Year Badge -->
                              @if (article.year) {
                                <span
                                  class="badge badge-custom badge-sm mr-2">
                                  {{ article.year }}
                                </span>
                              }
                              <!-- Volume Badge -->
                              @if (article.volume) {
                                <span
                                  class="badge badge-custom badge-sm mr-2">
                                  {{ 'ARTICLE_LBL_VOLUME' | translate }}: {{ article.volume }}
                                </span>
                              }
                              <!-- Issue Badge -->
                              @if (article.issue) {
                                <span
                                  class="badge badge-custom badge-sm mr-2">
                                  {{ 'ARTICLE_LBL_NUMBER' | translate }}: {{ article.issue }}
                                </span>
                              }
                              <!-- Page Range Badge -->
                              @if (article.startPage && article.endPage) {
                                <span
                                  class="badge badge-custom badge-sm mr-2">
                                  {{ 'ARTICLE_LBL_SHORTCUT_PAGE' | translate }}. {{ article.startPage }}-{{ article.endPage }}
                                </span>
                              }
                              <!-- Language Badge -->
                              @if (article.localeText) {
                                <span
                                  class="badge badge-custom badge-sm mr-2 text-uppercase">
                                  {{ getLanguageCode(article.localeText) }}
                                </span>
                              }
                            </div>
                          </div>
                          <!-- Keywords Section -->
                          @if (article.keywords && article.keywords.length > 0) {
                            <div class="col-md-12 mt-2">
                              <div class="keywords-section">
                                <small class="text-muted font-weight-semibold">
                                  <i class="fas fa-tags mr-1"></i>
                                  {{ 'ARTICLE_LBL_KEYWORDS' | translate }}:
                                </small>
                                @for (kw of article.keywords; track kw; let last = $last) {
                                  <span>
                                    <span class="keyword-badge"
                                      [class.active-keyword]="kw.toLowerCase() === keyword.toLowerCase()">
                                      {{ kw }}
                                    </span>
                                    @if (!last) {
                                      <span> </span>
                                    }
                                  </span>
                                }
                              </div>
                            </div>
                          }
                        </div>
                      </div>
                    </div>
                    <!-- Separator (except for last item) -->
                    @if (i < articles.length - 1) {
                      <hr class="article-separator">
                    }
                  </div>
                </div>
              }
            </div>
          </article>
        </div>
      }

    </div>

  </div>
</div>
